import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { layersAtom, selectedLayerIdsAtom } from '@/atoms/layers.atom';
import { clipBoxStateAtom, viewerEvents, viewerStateAtomFamily } from '@/atoms/viewer.atom';
import {
  deserializeClipBox,
  filterVisibleLayerIds,
  normalizeCamera,
  serializeClipBox,
  type SavedViewCamera,
  type SavedViewClipBox,
} from '@/lib/savedViews';

/** A fully-captured view payload, ready to feed into a create/update mutation. */
export type CapturedView = {
  camera: SavedViewCamera;
  clipBox: SavedViewClipBox;
  visibleLayerIds: string[];
  baseMapId: string | null;
};

/** The minimum a saved-view-like object needs to be applied. */
type ApplicableView = {
  camera: unknown;
  clipBox: unknown;
  visibleLayerIds: string[];
  baseMapId?: string | null;
};

/**
 * Capture/restore the viewer state that makes up a "saved view" — camera,
 * clip box, layer visibility and active basemap. Lives in a shared hook so it
 * works from anywhere (the Layer Manager section, the Viewer deep-link), not
 * just inside `Viewer`.
 *
 * Apply is decoupled from `Viewer`'s local ortho/third-person `useState`: it
 * emits `applyCameraState`, which `Viewer` listens to for the camera-mode
 * flags and the in-`Canvas` `Controls` consumer listens to for the actual
 * camera transform (it waits until the projection type matches before
 * applying). See `app/src/components/Viewer.tsx`.
 */
export const useViewCaptureApply = (projectId: string) => {
  const clipBoxState = useAtomValue(clipBoxStateAtom(projectId));
  const selectedLayerIds = useAtomValue(selectedLayerIdsAtom(projectId));
  const viewerState = useAtomValue(viewerStateAtomFamily(projectId));
  const allLayers = useAtomValue(layersAtom);
  const setSelectedLayerIds = useSetAtom(selectedLayerIdsAtom(projectId));
  const setClipBoxState = useSetAtom(clipBoxStateAtom(projectId));
  const setViewerState = useSetAtom(viewerStateAtomFamily(projectId));

  const captureCurrentView = useCallback((): CapturedView | null => {
    try {
      const raw = sessionStorage.getItem(`@actegon.gis.cameraState.${projectId}`);
      if (!raw) return null;
      return {
        camera: normalizeCamera(JSON.parse(raw)),
        clipBox: serializeClipBox(clipBoxState),
        visibleLayerIds: selectedLayerIds,
        baseMapId: viewerState.activeBaseMapId,
      };
    } catch {
      return null;
    }
  }, [projectId, clipBoxState, selectedLayerIds, viewerState.activeBaseMapId]);

  const applyView = useCallback(
    (view: ApplicableView) => {
      const camera = view.camera as SavedViewCamera;
      const clip = view.clipBox as SavedViewClipBox;

      // Layer visibility — drop ids of layers that no longer exist.
      setSelectedLayerIds(
        filterVisibleLayerIds(
          view.visibleLayerIds,
          allLayers.map((l) => l.id),
        ),
      );
      // Clip box — deserialize forces interactionMode: 'VIEW'.
      setClipBoxState(deserializeClipBox(clip));
      // Basemap — Viewer's existing effect falls back if it no longer exists.
      setViewerState((prev) => ({ ...prev, activeBaseMapId: view.baseMapId ?? null }));
      // Camera — Viewer sets ortho/third-person from this event; the Controls
      // frame-consumer applies the transform once the projection type matches.
      viewerEvents.emit('applyCameraState', camera);
    },
    [setSelectedLayerIds, allLayers, setClipBoxState, setViewerState],
  );

  return { captureCurrentView, applyView };
};
